// ConfidenceChip.jsx — #34: surface xirr_confidence as a small labeled chip with a one-line
// tooltip per state. null XIRR with needs_nav reads "Awaiting NAV", not an error.
//
// KNOWN GAP (flagged, not silently hidden — see final build report): the dumb bridge
// (pg_poc_bridge.overview_from_poc / cashflow_from_poc) reads xirr_confidence off
// ledger_poc_metrics internally but never places it on the member/position dict or the /cashflow
// kpis dict it returns — only the POST /poc-inspection/input response's `projection` field
// carries it today. This chip renders whenever xirr_confidence is present on the data it's given
// (post-edit liveProjection, or a future backend passthrough) and renders nothing otherwise —
// never a fake/guessed state.

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";

// One entry per value ledger_poc/project.py's xirr_confidence can take (see project.py:250-314).
const XIRR_CONFIDENCE_META = {
  computed: {
    label: "Computed",
    tone: "emerald",
    tip: "Calculated from a complete, dated cash-flow history.",
  },
  estimate: {
    label: "Estimate",
    tone: "indigo",
    tip: "Call timing is uncertain, so this return rate is an estimate.",
  },
  computed_stale_nav: {
    label: "Stale NAV",
    tone: "amber",
    tip: "The valuation on file is from an older statement — add a current NAV to refresh this.",
  },
  underdetermined: {
    label: "Underdetermined",
    tone: "amber",
    tip: "Not enough distinct call timing to compute a reliable return rate.",
  },
  uncomputable: {
    label: "Uncomputable",
    tone: "gray",
    tip: "The cash-flow history on file doesn't support a return-rate calculation.",
  },
  needs_nav: {
    label: "Awaiting NAV",
    tone: "amber",
    tip: "No current valuation is on file yet — add one to unlock this metric.",
  },
  realized_no_residual: {
    label: "Realized",
    tone: "emerald",
    tip: "Position closed with no residual value; return rate computed on realized cash flows.",
  },
  realized_uncomputable: {
    label: "Realized",
    tone: "gray",
    tip: "Position closed, but the cash-flow history doesn't support a return-rate calculation.",
  },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-300",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-300",
  amber: "bg-amber-50 text-amber-700 border-amber-400",
  gray: "bg-gray-100 text-gray-600 border-gray-300",
};

export function xirrConfidenceMeta(key) {
  return XIRR_CONFIDENCE_META[key] || null;
}

/** Small labeled chip for one xirr_confidence state, with a one-line hover tooltip. Renders
 * nothing when `confidence` is null/undefined/unrecognized — never a fabricated label. */
export default function ConfidenceChip({ confidence }) {
  const meta = xirrConfidenceMeta(confidence);
  if (!meta) return null;
  const cls = TONE_CLASSES[meta.tone] ?? TONE_CLASSES.gray;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold cursor-default select-none",
            cls
          )}
          data-testid="xirr-confidence-chip"
          data-confidence={confidence}
        >
          {meta.label}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-56 text-xs text-gray-700 leading-relaxed">
        {meta.tip}
      </HoverCardContent>
    </HoverCard>
  );
}
